import type { OptionSheetOption } from '@frontend/api-types';
import { useEffect, useState } from '@frontend/react';
import { Button, Icon, OptionDetailModal, OptionSheetCard, Text, XStack, YStack, showToast } from '@frontend/web-react';
import { captureSentryException } from '@frontend/web-utils';
import { useRetrieveOptionSheetQuery, useReviewOptionSheetMutation } from '@frontend/web/hooks';

export type TaskChatOptionSheetCarouselProps = {
  onSendMessage?: (message: string) => Promise<void> | void;
  optionSheetId: string;
  taskId: string;
};

export const TaskChatOptionSheetCarousel = ({
  onSendMessage,
  optionSheetId,
  taskId,
}: TaskChatOptionSheetCarouselProps) => {
  const { data: optionSheet } = useRetrieveOptionSheetQuery(optionSheetId);
  const [reviewOptionSheet, { isLoading: isReviewLoading }] = useReviewOptionSheetMutation();
  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isGoingDeeper, setIsGoingDeeper] = useState(false);
  const [detailOption, setDetailOption] = useState<OptionSheetOption | null>(null);
  const [hasHydrated, setHasHydrated] = useState(false);

  const options = optionSheet?.groups[0]?.options ?? [];
  const currency = optionSheet?.currency;
  const isInteractionDisabled = isSubmitted || isGoingDeeper;

  useEffect(() => {
    if (hasHydrated) return;
    if (optionSheet?.selectedOptionIds?.length) {
      setSelectedOptionIds(optionSheet.selectedOptionIds);
      setHasHydrated(true);
      if (optionSheet.reviewedAt) {
        setIsSubmitted(true);
      }
    }
  }, [optionSheet?.selectedOptionIds, optionSheet?.reviewedAt, hasHydrated]);

  const handleToggleOption = (optionId: string) => {
    if (isInteractionDisabled) return;
    setSelectedOptionIds((prev) =>
      prev.includes(optionId) ? prev.filter((id) => id !== optionId) : [...prev, optionId],
    );
  };

  const handleSubmit = async () => {
    try {
      await reviewOptionSheet({
        optionSheetId,
        reviewFeedback: '',
        selectedOptionIds,
        taskId,
      }).unwrap();
      setIsSubmitted(true);
    } catch (error) {
      captureSentryException(error);
      showToast({ title: 'Failed to submit selection. Please try again.', variant: 'danger' });
    }
  };

  const handleGoDeeper = async () => {
    try {
      await onSendMessage?.("These options don't quite match what I'm looking for. Can you show me different options?");
      setIsGoingDeeper(true);
    } catch (error) {
      captureSentryException(error);
      showToast({ title: 'Failed to send message. Please try again.', variant: 'danger' });
    }
  };

  if (!optionSheet) return null;

  return (
    <YStack className="gap-md">
      <OptionDetailModal currency={currency} onClose={() => setDetailOption(null)} option={detailOption} />
      {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
      <div className="flex snap-x snap-mandatory gap-md overflow-x-auto pb-sm">
        {options.map((option) => (
          <OptionSheetCard
            currency={currency}
            disabled={isInteractionDisabled}
            isSelected={selectedOptionIds.includes(option.id)}
            key={option.id}
            onOpenDetails={() => setDetailOption(option)}
            onToggle={() => handleToggleOption(option.id)}
            option={option}
          />
        ))}
      </div>

      {!isSubmitted && !isGoingDeeper ? (
        <YStack className="gap-sm">
          {selectedOptionIds.length > 0 ? (
            <Text color="secondary" type="p-sm">
              {selectedOptionIds.length} {selectedOptionIds.length === 1 ? 'option' : 'options'} selected
            </Text>
          ) : null}
          <XStack className="gap-sm">
            <Button
              disabled={selectedOptionIds.length === 0}
              isLoading={isReviewLoading}
              label="Submit selections"
              onClick={() => {
                void handleSubmit();
              }}
              variant="primary"
            />
            <Button label="Go deeper" onClick={handleGoDeeper} variant="secondary" />
          </XStack>
        </YStack>
      ) : (
        <XStack className="items-center gap-xs">
          <Icon color="success" name="IconCircleCheck" size={20} />
          <Text color="success" type="p-sm">
            {isSubmitted
              ? `${selectedOptionIds.length} ${selectedOptionIds.length === 1 ? 'option' : 'options'} submitted`
              : 'Looking for more options...'}
          </Text>
        </XStack>
      )}
    </YStack>
  );
};
